import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DirectusService } from '../core/services/directus.service';

interface TicketFormModel {
  name: string;
  description: string;
  pricingMethod: 'fixed' | 'free';
  price: number;
  quantityMode: 'unlimited' | 'limited';
  quantityTotal: number | null;
  visibility: 'show' | 'hide';
}

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <section class="ticket-create-page">
      <header class="page-head">
        <h1>{{ ticketId ? 'Edit Ticket' : 'New Ticket' }}</h1>
        <div class="actions">
          <button type="button" class="btn" (click)="cancel()" [disabled]="creating">Cancel</button>
          <button type="button" class="btn btn-primary" (click)="createTicket()" [disabled]="creating">
            {{ creating ? (ticketId ? 'Saving...' : 'Creating...') : (ticketId ? 'Save Ticket' : 'Create Ticket') }}
          </button>
        </div>
      </header>

      <p class="error" *ngIf="error">{{ error }}</p>

      <section class="form-section">
        <h2>Ticket info</h2>
        <label>
          Ticket name
          <select [(ngModel)]="form.name">
            <option *ngFor="let nameOption of ticketNameOptions" [value]="nameOption">{{ nameOption }}</option>
          </select>
        </label>
        <label>
          Description
          <textarea rows="3" [(ngModel)]="form.description"></textarea>
        </label>
      </section>

      <section class="form-section">
        <h2>Pricing method</h2>
        <div class="mode-row">
          <label><input type="radio" name="pricingMethod" value="fixed" [(ngModel)]="form.pricingMethod" /> Fixed price</label>
          <label><input type="radio" name="pricingMethod" value="free" [(ngModel)]="form.pricingMethod" /> Free</label>
        </div>
        <label>
          Ticket price
          <input type="number" min="0" [(ngModel)]="form.price" [disabled]="form.pricingMethod === 'free'" />
        </label>
      </section>

      <section class="form-section">
        <h2>Ticket quantity</h2>
        <div class="mode-row">
          <label><input type="radio" name="quantityMode" value="unlimited" [(ngModel)]="form.quantityMode" /> Unlimited number of tickets</label>
          <label><input type="radio" name="quantityMode" value="limited" [(ngModel)]="form.quantityMode" /> Limited number of tickets</label>
        </div>
        <label *ngIf="form.quantityMode === 'limited'">
          Quantity total
          <input type="number" min="1" [(ngModel)]="form.quantityTotal" />
        </label>
      </section>

      <section class="form-section">
        <h2>Advanced settings (optional)</h2>
        <label>
          Ticket visibility
          <select [(ngModel)]="form.visibility">
            <option value="show">Show ticket</option>
            <option value="hide">Hide ticket</option>
          </select>
        </label>
      </section>
    </section>
  `,
  styles: [
    `
      .ticket-create-page {
        max-width: 980px;
        margin: 0 auto;
        display: grid;
        gap: 0.8rem;
      }
      .page-head {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 0.75rem;
      }
      h1 {
        margin: 0;
        font-size: 1.8rem;
      }
      .actions {
        display: flex;
        gap: 0.45rem;
      }
      .btn {
        border: 1px solid #bfdbfe;
        background: #ffffff;
        color: #1d4ed8;
        border-radius: 999px;
        padding: 0.42rem 0.8rem;
        font-weight: 700;
        cursor: pointer;
      }
      .btn-primary {
        background: #2563eb;
        color: #ffffff;
        border-color: #2563eb;
      }
      .form-section {
        border: 1px solid #dbe3ee;
        border-radius: 12px;
        background: #ffffff;
        padding: 0.85rem;
        display: grid;
        gap: 0.65rem;
      }
      .form-section h2 {
        margin: 0;
        font-size: 1.02rem;
      }
      label {
        display: grid;
        gap: 0.35rem;
        color: #334155;
        font-weight: 600;
      }
      input,
      select,
      textarea {
        border: 1px solid #bfdbfe;
        border-radius: 8px;
        padding: 0.5rem 0.62rem;
        font: inherit;
      }
      .mode-row {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 0.55rem;
      }
      .mode-row label {
        display: flex;
        align-items: center;
        gap: 0.45rem;
        border: 1px solid #dbe3ee;
        border-radius: 8px;
        padding: 0.55rem 0.6rem;
        background: #f8fafc;
        font-weight: 600;
      }
      .error {
        margin: 0;
        color: #b91c1c;
        font-weight: 700;
      }
      @media (max-width: 900px) {
        .mode-row {
          grid-template-columns: 1fr;
        }
      }
    `
  ]
})
export class EventTicketCreateComponent implements OnInit {
  private static readonly ALLOWED_RETURN_TABS = new Set([
    'overview',
    'tickets-seating',
    'settings',
    'features',
    'orders',
    'guests'
  ]);

  readonly ticketNameOptions = ['JCI Nusantara', 'JCI Indonesia', 'Non-members', 'VIP', 'Sponsorships'];
  form: TicketFormModel = this.emptyForm();
  eventId = '';
  ticketId = '';
  returnTab = 'tickets-seating';
  creating = false;
  error = '';

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly directus: DirectusService
  ) {}

  ngOnInit(): void {
    this.eventId = String(this.route.snapshot.paramMap.get('id') || '');
    this.ticketId = String(this.route.snapshot.queryParamMap.get('ticketId') || '');
    this.returnTab = this.parseReturnTab(this.route.snapshot.queryParamMap.get('returnTab'));
    if (!this.eventId) {
      void this.router.navigateByUrl('/events/published');
      return;
    }
    if (this.ticketId) {
      void this.loadTicketForEdit();
    }
  }

  cancel(): void {
    if (!this.eventId) return;
    void this.router.navigateByUrl(`/events/${encodeURIComponent(this.eventId)}/${this.returnTab}`);
  }

  async createTicket(): Promise<void> {
    this.error = '';
    if (!this.eventId) {
      this.error = 'Missing event ID.';
      return;
    }
    if (!this.form.name.trim()) {
      this.error = 'Ticket name is required.';
      return;
    }
    if (this.form.pricingMethod === 'fixed' && this.form.price < 0) {
      this.error = 'Ticket price must be 0 or higher.';
      return;
    }
    if (this.form.quantityMode === 'limited' && (!this.form.quantityTotal || this.form.quantityTotal < 1)) {
      this.error = 'Quantity total must be at least 1.';
      return;
    }

    this.creating = true;
    try {
      const payload = {
        event_id: this.eventId,
        name: this.form.name.trim(),
        description: this.form.description.trim() || null,
        description_footer: this.form.description.trim() || null,
        pricing_method: this.form.pricingMethod,
        price: this.form.pricingMethod === 'free' ? 0 : this.form.price,
        quantity_mode: this.form.quantityMode,
        quantity_total: this.form.quantityMode === 'limited' ? this.form.quantityTotal || 0 : null,
        status: this.form.visibility === 'hide' ? 'hidden' : 'available'
      };

      if (this.ticketId) {
        await this.directus.update('event_tickets', this.ticketId, payload);
      } else {
        const existing = await this.directus.list('event_tickets', {
          fields: 'id',
          'filter[event_id][_eq]': this.eventId,
          limit: 500
        });

        await this.directus.create('event_tickets', {
          ...payload,
          sort: existing.length + 1
        });
      }
      void this.router.navigateByUrl(`/events/${encodeURIComponent(this.eventId)}/${this.returnTab}`);
    } catch (error: unknown) {
      if (error instanceof HttpErrorResponse) {
        const reason =
          (error.error as { errors?: Array<{ message?: string; extensions?: { reason?: string } }> })?.errors?.[0]?.extensions?.reason ||
          (error.error as { errors?: Array<{ message?: string }> })?.errors?.[0]?.message ||
          error.message;
        this.error = reason
          ? `Failed to ${this.ticketId ? 'save' : 'create'} ticket: ${reason}`
          : `Failed to ${this.ticketId ? 'save' : 'create'} ticket.`;
      } else {
        this.error = `Failed to ${this.ticketId ? 'save' : 'create'} ticket.`;
      }
    } finally {
      this.creating = false;
    }
  }

  private emptyForm(): TicketFormModel {
    return {
      name: 'JCI Nusantara',
      description: 'This is your event ticket.',
      pricingMethod: 'fixed',
      price: 0,
      quantityMode: 'unlimited',
      quantityTotal: null,
      visibility: 'show'
    };
  }

  private async loadTicketForEdit(): Promise<void> {
    if (!this.ticketId) return;
    this.error = '';
    try {
      const rows = await this.directus.list('event_tickets', {
        fields: 'id,name,description,description_footer,price,pricing_method,quantity_mode,quantity_total,status',
        'filter[id][_eq]': this.ticketId,
        limit: 1
      });
      const row = rows[0];
      if (!row) {
        this.error = 'Ticket not found.';
        return;
      }
      const pricingRaw = typeof row['pricing_method'] === 'string' ? row['pricing_method'] : 'fixed';
      const modeRaw = typeof row['quantity_mode'] === 'string' ? row['quantity_mode'] : 'unlimited';
      const qtyRaw = row['quantity_total'];
      const statusRaw = typeof row['status'] === 'string' ? row['status'] : 'available';
      const nameRaw = typeof row['name'] === 'string' ? row['name'] : this.form.name;
      const descriptionRaw =
        typeof row['description_footer'] === 'string' && row['description_footer'].trim().length
          ? row['description_footer']
          : typeof row['description'] === 'string'
            ? row['description']
            : '';
      const priceRaw = row['price'];

      this.form = {
        ...this.form,
        name: nameRaw,
        description: descriptionRaw,
        pricingMethod: pricingRaw === 'free' ? 'free' : 'fixed',
        price: typeof priceRaw === 'number' ? priceRaw : Number(priceRaw || 0),
        quantityMode: modeRaw === 'limited' ? 'limited' : 'unlimited',
        quantityTotal: modeRaw === 'limited' ? (typeof qtyRaw === 'number' ? qtyRaw : Number(qtyRaw || 0)) : null,
        visibility: statusRaw.trim().toLowerCase() === 'hidden' ? 'hide' : 'show'
      };
    } catch (error: unknown) {
      if (error instanceof HttpErrorResponse) {
        const reason =
          (error.error as { errors?: Array<{ message?: string; extensions?: { reason?: string } }> })?.errors?.[0]?.extensions?.reason ||
          (error.error as { errors?: Array<{ message?: string }> })?.errors?.[0]?.message ||
          error.message;
        this.error = reason ? `Failed to load ticket: ${reason}` : 'Failed to load ticket. Please refresh and try again.';
      } else {
        this.error = 'Failed to load ticket. Please refresh and try again.';
      }
    }
  }

  private parseReturnTab(rawValue: string | null): string {
    const tab = String(rawValue || '').trim();
    return EventTicketCreateComponent.ALLOWED_RETURN_TABS.has(tab) ? tab : 'tickets-seating';
  }
}
